import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, ExternalLink, PlayCircle, Share } from 'lucide-react';
import { Card, Section } from './primitives';
import { shareFile } from '../../../utils/shareFile';
import { notify } from '../../../utils/notify';

// Small building blocks for interactive protocol guides (calculators + reference
// text). Styled to match the rotation tabs: flat cards, hairline dividers, the
// hospital colour only as a tint.

/** Segmented control. `small` for secondary choices inside a card. */
export const Segmented = ({ items, value, onChange, small }) => (
  <div
    className="bg-gray-100 dark:bg-gray-800 rounded-xl p-1 grid gap-1"
    style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}
    role="tablist"
  >
    {items.map((t) => (
      <button
        key={t.id}
        role="tab"
        aria-selected={value === t.id}
        onClick={() => { onChange(t.id); }}
        className={`${small ? 'py-1.5 text-xs' : 'py-2 text-xs'} px-1 rounded-lg leading-tight transition-all duration-200 active:scale-95 ${
          value === t.id ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-md font-bold animate-pop' : 'font-medium text-gray-500 dark:text-gray-400'
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

/** Labelled numeric input */
export const Field = ({ label, value, onChange, unit, placeholder, accent, optional }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
      {label}{optional && <span className="font-normal text-gray-400"> · optional</span>}
    </span>
    <span className="mt-1 flex items-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus-within:border-transparent focus-within:ring-2" style={{ '--tw-ring-color': accent }}>
      <input
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-[17px] text-gray-900 dark:text-white placeholder:text-gray-300 dark:placeholder:text-gray-600 focus:outline-none"
      />
      {unit && <span className="pr-3 text-sm text-gray-400">{unit}</span>}
    </span>
  </label>
);

/** Label on the left, value on the right, with an optional muted detail under the value */
export const Result = ({ label, value, detail, tone }) => {
  const toneCls = tone === 'warn'
    ? 'text-amber-700 dark:text-amber-300'
    : tone === 'danger'
      ? 'text-red-700 dark:text-red-300'
      : 'text-gray-900 dark:text-white';
  return (
    <div className="px-4 py-3 flex items-baseline justify-between gap-4">
      <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
      <span className="text-right">
        <span className={`text-[15px] font-semibold ${toneCls}`}>{value}</span>
        {detail && <span className="block text-xs text-gray-400 dark:text-gray-500 mt-0.5">{detail}</span>}
      </span>
    </div>
  );
};

/** Inline note inside a card: neutral, amber (caution) or red (danger) */
export const Note = ({ children, tone = 'neutral' }) => {
  const cls = {
    neutral: 'bg-gray-50 dark:bg-gray-900/40 text-gray-700 dark:text-gray-300',
    warn: 'bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100',
    danger: 'bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100',
  }[tone];
  return <div className={`px-4 py-3 text-sm leading-snug ${cls}`}>{children}</div>;
};

/** Plain bullet list for a card row */
export const Bullets = ({ items, className = '' }) => (
  <ul className={`space-y-1 ${className}`}>
    {items.map((item) => (
      <li key={typeof item === 'string' ? item : item.text} className="text-sm text-gray-700 dark:text-gray-300 leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[0.55em] before:w-1 before:h-1 before:rounded-full before:bg-gray-400">
        {typeof item === 'string' ? item : (
          <>
            {item.text}
            {item.warn && <span className="block text-red-700 dark:text-red-300">{item.warn}</span>}
          </>
        )}
      </li>
    ))}
  </ul>
);

/** A card row with a bold lead line and bullets under it */
export const StepRow = ({ title, items }) => (
  <div className="px-4 py-3">
    {title && <div className="text-[15px] font-medium text-gray-900 dark:text-white">{title}</div>}
    <Bullets items={items} className={title ? 'mt-1' : ''} />
  </div>
);

/** Collapsed-by-default card tinted with the hospital colour, for background reading */
export const AboutCard = ({ title, accent, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border shadow-card overflow-hidden" style={{ borderColor: `${accent}55` }}>
      <button
        onClick={() => { setOpen((v) => !v); }}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
        style={{ backgroundColor: `${accent}1f` }}
        aria-expanded={open}
      >
        <span className="font-semibold text-[#8a7a61] dark:text-[#C4B5A0]">{title}</span>
        {open ? <ChevronUp size={18} style={{ color: accent }} /> : <ChevronDown size={18} style={{ color: accent }} />}
      </button>
      {open && <div className="p-4 space-y-3 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{children}</div>}
    </div>
  );
};

/** Collapsed list of references, numbered, small type */
export const References = ({ items }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card>
      <button onClick={() => { setOpen((v) => !v); }} className="w-full px-4 py-3 flex items-center justify-between text-left" aria-expanded={open}>
        <span className="text-[15px] font-medium text-gray-900 dark:text-white">References</span>
        {open ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
      </button>
      {open && (
        <ol className="px-4 py-3 space-y-2 list-decimal list-inside">
          {items.map((r) => <li key={r} className="text-xs text-gray-500 dark:text-gray-400 leading-snug">{r}</li>)}
        </ol>
      )}
    </Card>
  );
};

/** Link to the original document the guide was built from */
export const SourceRow = ({ href, title = 'Original protocol (PDF)', updated, detail, accent, kind = 'pdf' }) => {
  const [busy, setBusy] = useState(false);
  const sharable = kind === 'pdf' && /\.pdf(\?|$)/i.test(href || '');
  const share = async (e) => {
    e.preventDefault();
    setBusy(true);
    const result = await shareFile(href, title);
    setBusy(false);
    if (result === 'downloaded') notify.success('Downloaded');
  };
  return (
    <Card>
      <div className="flex items-stretch">
        <a href={href} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-0 pl-4 py-3 flex items-center gap-3">
          {kind === 'video' ? <PlayCircle size={18} className="flex-shrink-0" style={{ color: accent }} /> : <FileText size={18} className="flex-shrink-0" style={{ color: accent }} />}
          <span className="flex-1 min-w-0">
            <span className="block text-[15px] font-medium text-gray-900 dark:text-white">{title}</span>
            {(updated || detail) && <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{detail || `Last updated ${updated}`}</span>}
          </span>
          {!sharable && <ExternalLink size={14} className="text-gray-400 flex-shrink-0 mr-4" />}
        </a>
        {sharable && (
          <button onClick={share} disabled={busy} aria-label={`Share ${title}`} className="px-4 flex items-center text-gray-300 dark:text-gray-600 hover:text-gray-500 disabled:opacity-50">
            <Share size={18} className={busy ? 'animate-pulse' : ''} />
          </button>
        )}
      </div>
    </Card>
  );
};

/**
 * Collapsed rows that open in place, the pattern from the Contacts "More" card:
 * closed groups stack as rows in one card; a tapped group becomes its own
 * Section + Card with a collapse chevron, keeping its position in the list.
 *   groups: [{ id, title, aside?, content }]   content: rendered inside the Card when open
 */
export const ExpandableGroups = ({ groups, rowsTitle, tone = {} }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => {
    setOpen((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };
  const segments = [];
  groups.forEach((g) => {
    if (open.has(g.id)) segments.push({ type: 'group', group: g });
    else if (segments.length && segments[segments.length - 1].type === 'rows') segments[segments.length - 1].groups.push(g);
    else segments.push({ type: 'rows', groups: [g] });
  });
  return (
    <>
      {segments.map((seg, i) => seg.type === 'group' ? (
        <Section
          key={seg.group.id}
          title={seg.group.title}
          aside={<button onClick={() => toggle(seg.group.id)} aria-label={`Collapse ${seg.group.title}`} className="p-1 -m-1 text-gray-400"><ChevronUp size={16} /></button>}
        >
          <Card>{seg.group.content}</Card>
        </Section>
      ) : (
        <Section key={`rows-${i}`} title={i === 0 ? rowsTitle : undefined}>
          <Card>
            {seg.groups.map((g) => (
              <button key={g.id} onClick={() => toggle(g.id)} aria-expanded={false} className="w-full flex items-center gap-3 px-4 py-3 text-left">
                <span className={`flex-1 text-[15px] font-medium ${g.danger ? 'text-red-800 dark:text-red-200' : 'text-gray-900 dark:text-white'}`}>{g.title}</span>
                {g.aside && <span className="text-xs text-gray-400 dark:text-gray-500 truncate max-w-[45%]">{g.aside}</span>}
                <ChevronDown size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />
              </button>
            ))}
          </Card>
        </Section>
      ))}
    </>
  );
};
